import { useAtomValue } from 'jotai';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { MultiSelect } from '@/components/ui/multi-select';
import { FacebookIcon, InstagramIcon } from '@/components/icons/platform-icons';
import { toast } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { socialAgentService } from '@/services/social-agent.service';
import { RULE_FORM_CONFIG, canEditMeaning } from '../rule-form-config';
import { channelLabel, channelScopeHint, isIrreversibleAction } from '../constants';
import { buildChannelScope } from '../channel-scope';
import { DIALOG_SHELL, DIALOG_TITLE, FIELD, FIELD_HINT, FIELD_LABEL, FIELD_TRIGGER, PILL_BUTTON, TEXTAREA, FIELD_HINT_STRONG} from '../ui-chrome';
import { TagCombobox } from './tag-combobox';
import type { SocialActionType, SocialRule, SocialRuleActionOptions } from '../types';

interface RuleFormDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** null = creating. */
	rule: SocialRule | null;
	/** Which form to show when creating; ignored when editing. */
	actionType: SocialActionType;
	channels: { channelId: string; handle: string; platform: 'facebook' | 'instagram' }[];
	/** Tags already used by other rules, offered as picks. Tags are free text, so this is not a whitelist. */
	tagSuggestions?: string[];
	onSaved: () => void;
}

interface FormState {
	name: string;
	condition: string;
	replyText: string;
	dynamicReply: boolean;
	channelIds: string[];
	tags: string[];
	like: boolean;
	closeAfterAction: boolean;
}

const FIELD_IDS = {
	name: 'social-rule-name',
	condition: 'social-rule-condition',
	replyText: 'social-rule-reply-text'
} as const;

const emptyState: FormState = {
	name: '',
	condition: '',
	replyText: '',
	dynamicReply: false,
	channelIds: [],
	tags: [],
	like: false,
	closeAfterAction: false,
};

const stateFromRule = (rule: SocialRule | null): FormState =>
	rule
		? {
				name: rule.name,
				condition: rule.condition,
				replyText: rule.actionOptions.replyText ?? '',
				dynamicReply: rule.actionOptions.dynamicReply === true,
				channelIds: rule.channelScope.map((scope) => scope.channelId),
				tags: rule.actionOptions.tags ?? [],
				like: rule.actionOptions.like ?? false,
				closeAfterAction: rule.actionOptions.closeAfterAction ?? false
			}
		: emptyState;

/**
 * The fields a save cannot go without.
 *
 * Reply copy joins name and condition for a rule that posts publicly: it is the only thing that
 * says what Kim replies, and the server refuses such a rule without it.
 */
const hasRequiredFields = (form: FormState, replyRequired: boolean, tagsRequired: boolean): boolean => {
	const meaningFilled = Boolean(form.name.trim() && form.condition.trim());
	if (!meaningFilled) return false;
	if (replyRequired && !form.replyText.trim()) return false;
	// A tag that is only whitespace is not a tag.
	if (tagsRequired && !form.tags.some((tag) => tag.trim())) return false;
	return true;
};


/**
 * A labelled toggle row, matching the design's `Label ————— [switch]` layout.
 *
 * With a `hint` it is the two-line row of Figma 6256:27355: Caption/Medium over Micro/Regular,
 * 2px apart, both in text/secondary.
 */
const ToggleRow = ({
	label,
	hint,
	checked,
	disabled,
	onCheckedChange
}: {
	label: string;
	hint?: string;
	checked: boolean;
	disabled?: boolean;
	onCheckedChange: (checked: boolean) => void;
}) => (
	<div className={cn('flex items-center justify-between', disabled && 'opacity-50')}>
		{hint ? (
			<div className='flex flex-col gap-0.5'>
				<Label className={cn(FIELD_LABEL, 'leading-4')}>{label}</Label>
				<p className={cn(FIELD_HINT, 'text-gray-900')}>{hint}</p>
			</div>
		) : (
			<Label className={FIELD_LABEL}>{label}</Label>
		)}
		<Switch checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} aria-label={label} />
	</div>
);

export const RuleFormDialog = ({
	open,
	onOpenChange,
	rule,
	actionType,
	channels,
	tagSuggestions = [],
	onSaved
}: RuleFormDialogProps) => {
	const effectiveAction = rule?.actionType ?? actionType;
	const config = RULE_FORM_CONFIG[effectiveAction];
	const editable = canEditMeaning(rule, effectiveAction);

	const [form, setForm] = useState<FormState>(emptyState);
	const [saving, setSaving] = useState<'draft' | 'enable' | null>(null);
	// Set once a create succeeds. A step after it can still fail — enabling the rule, say — which
	// leaves the dialog open, and without this the retry would create a duplicate rule instead of
	// amending the one just made.
	const [createdRule, setCreatedRule] = useState<SocialRule | null>(null);

	const targetRule = rule ?? createdRule;

	// Reset on open rather than on rule change: the dialog stays mounted between openings, so a
	// stale draft from the previous rule would otherwise be shown for the next one.
	useEffect(() => {
		if (!open) return;
		setForm(stateFromRule(rule));
		setCreatedRule(null);
	}, [open, rule]);

	/**
	 * Each channel carries its platform glyph.
	 *
	 * A Page and an Instagram account are routinely named the same thing — a brand connects
	 * "Kim.cc" on both — so a list of bare names offers two entries that scope the rule to
	 * different places and read as a duplicate. The icon is the only thing that tells them apart
	 * before you pick one.
	 */
	const channelOptions = useMemo(
		() =>
			channels.map((channel) => ({
				label: channelLabel(channel),
				value: channel.channelId,
				icon: channel.platform === 'facebook' ? FacebookIcon : InstagramIcon
			})),
		[channels]
	);

	const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((current) => ({ ...current, [key]: value }));
	};

	const channelScope = buildChannelScope(form.channelIds, channels, rule);

	/** No channels picked means every channel, now and in future — see `channelScopeHint`. */
	const unscoped = form.channelIds.length === 0;

	// A dynamic reply is written by Kim per comment, so the rule carries no copy of its own.
	const dynamicReply = config.replyText && form.dynamicReply;
	const replyRequired = config.replyText && !dynamicReply;
	const tagsRequired = config.tags === 'required';
	const canSave = editable ? hasRequiredFields(form, replyRequired, tagsRequired) : true;

	/** Only the options this action's form actually offers; the rest must stay absent, not false. */
	const actionOptions: SocialRuleActionOptions = {
		replyText: config.replyText && !dynamicReply ? form.replyText : undefined,
		// Only when on — see `SocialRuleActionOptions.dynamicReply`.
		dynamicReply: dynamicReply ? true : undefined,
		tags: form.tags,
		like: config.like ? form.like : undefined,
		closeAfterAction: config.closeAfterAction ? form.closeAfterAction : undefined
	};

	const selectedBusinessUnitId = useAtomValue(selectedBusinessUnitIdAtom);
	const buScope = useMemo(
		() => (selectedBusinessUnitId ? { bu_id: selectedBusinessUnitId } : {}),
		[selectedBusinessUnitId]
	);

	const persist = (): Promise<SocialRule> => {
		if (targetRule) {
			// A Kim-authored rule rejects everything but channel scope, so send only that —
			// resubmitting its own unchanged name would be refused with a 403.
			return socialAgentService.updateRule(
				targetRule.id,
				editable ? { name: form.name, condition: form.condition, channelScope, actionOptions } : { channelScope }
			);
		}

		// The brand the rule belongs to. Only CREATE carries it: an update addresses a rule by id,
		// and that row already knows its own brand — sending one could only move it.
		return socialAgentService.createRule(
			{
				name: form.name,
				condition: form.condition,
				actionType: effectiveAction,
				channelScope,
				actionOptions
			},
			buScope
		);
	};

	const save = async (mode: 'draft' | 'enable') => {
		if (!canSave || saving) return;
		const amending = targetRule !== null;
		/**
		 * The second of two writes, and the reason this is not just an `await`.
		 *
		 * `persist()` has already succeeded by the time this runs, so a failure here is NOT "could
		 * not save the rule" — the edits are stored, and for a rule that was live the engine reads
		 * rules fresh per comment, so they are ALREADY moderating. Letting this fall into the outer
		 * catch produced the worst pairing available: the edit published, and the customer told it
		 * had not been.
		 *
		 * So it reports what actually happened and rethrows nothing. The caller carries on to
		 * `onSaved()`, because the list must show the rule as it now IS — and suppresses its own
		 * success toast on a `false`, since two contradictory toasts is worse than either alone.
		 */
		const applyStatus = async (saved: { id: string; name: string }, status: 'enabled' | 'draft'): Promise<boolean> => {
			try {
				await socialAgentService.setRuleStatus(saved.id, status);
				return true;
			} catch (err) {
				console.error('[RuleFormDialog.save] status write failed after a successful save', {
					ruleId: saved.id,
					status,
					err
				});
				toast.error(
					status === 'draft'
						? `Your changes to ${saved.name} were saved, but it is still live — moving it to Draft failed.`
						: `Your changes to ${saved.name} were saved, but turning it on failed.`
				);
				return false;
			}
		};

		setSaving(mode);

		try {
			const saved = await persist();
			setCreatedRule(saved);

			// Status is its own endpoint — the one write a Kim-authored rule accepts. TWO writes, and
			// the second can fail after the first has landed: see `applyStatus`.
			let statusApplied = true;
			if (mode === 'enable') statusApplied = await applyStatus(saved, 'enabled');

			// "Save as Draft" means draft, whatever the rule was before — it takes the rule OUT of
			// moderation and shows the Draft pill.
			//
			// It used to skip this for a live rule, to avoid silently stopping it moderating. That
			// was the wrong trade: the edits were saved to an still-enabled rule and the engine
			// reads rules fresh per comment, so pressing "Draft" on a live rule published the edit
			// instead of drafting it — the opposite of what the button says, on the one path where
			// being wrong reaches real customers.
			//
			// A NEW rule is already a draft (the server defaults to it), hence the status check.
			if (mode === 'draft' && saved.status !== 'draft') {
				statusApplied = await applyStatus(saved, 'draft');
			}

			// Unconditional: the rule was persisted, so the list is stale until it refetches —
			// whatever became of the status write. The dialog then stays OPEN on a failure, because
			// the status is the half still to fix and pressing the button again retries just that
			// (`createdRule` makes the retry an amend rather than a second create).
			onSaved();
			if (!statusApplied) return;

			toast.success(amending ? `Saved ${form.name || saved.name}` : `Created ${form.name}`);
			onOpenChange(false);
		} catch (err) {
			console.error('[RuleFormDialog.save] failed', { mode, ruleId: targetRule?.id ?? null, actionType: effectiveAction, err });
			toast.error(getApiErrorMessage(err, 'Could not save the rule'));
		} finally {
			setSaving(null);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* rounded-[20px], not the shared dialog's 24. A divider under the heading and NONE above
			    the footer — the review's "unnecessary lines" meant the footer rule alone, which is
			    also what Figma 4247:79282 shows. Content is a 24px stack inset 16px. */}
			{/*
			 * Focus the dialog, not the first field. Radix autofocuses the first tabbable element on
			 * open, and a text input receiving focus that way has its whole value selected — so
			 * opening a rule to read it put "Tag - Complaint" one keystroke from being wiped.
			 * preventDefault leaves focus on the content, which Radix makes focusable, so the
			 * focus trap and Escape still work.
			 */}
			<DialogContent
				showCloseButton={false}
				onOpenAutoFocus={(event) => event.preventDefault()}
				className={cn('max-w-[600px] w-full', DIALOG_SHELL)}
			>
				<DialogHeader className='h-[50px] justify-center border-b border-border px-3 py-2'>
					<DialogTitle className={DIALOG_TITLE}>{config.title}</DialogTitle>
				</DialogHeader>

				<div className='flex flex-col gap-6 px-4 py-6 max-h-[620px] overflow-auto'>
					<div className='flex flex-col gap-2'>
						<Label htmlFor={FIELD_IDS.name} className={FIELD_LABEL}>Rule Name</Label>
						<Input
							id={FIELD_IDS.name}
							className={FIELD}
							value={form.name}
							placeholder='Rule Name'
							disabled={!editable}
							onChange={(e) => set('name', e.target.value)}
						/>
					</div>

					<div className='flex flex-col gap-2'>
						<Label htmlFor={FIELD_IDS.condition} className={FIELD_LABEL}>{config.conditionLabel}</Label>
						<Textarea
							id={FIELD_IDS.condition}
							className={TEXTAREA}
							value={form.condition}
							placeholder='Type something...'
							rows={3}
							disabled={!editable}
							onChange={(e) => set('condition', e.target.value)}
						/>
					</div>

					{config.replyText && (
						<ToggleRow
							label='Want a dynamic reply?'
							hint='Replies adapt each time instead of staying fixed'
							checked={form.dynamicReply}
							disabled={!editable}
							onCheckedChange={(v) => set('dynamicReply', v)}
						/>
					)}

					{config.replyText && !dynamicReply && (
						<div className='flex flex-col gap-2'>
							<Label htmlFor={FIELD_IDS.replyText} className={FIELD_LABEL}>What to reply?</Label>
							<Textarea
								id={FIELD_IDS.replyText}
								className={TEXTAREA}
								value={form.replyText}
								placeholder='Type something...'
								rows={3}
								onChange={(e) => set('replyText', e.target.value)}
							/>
							{/* Nothing rewrites these words and nobody reads them at send time, so the brand has
							    to know it is writing the reply itself rather than briefing Kim on one. */}
							<p className={FIELD_HINT}>Kim posts this exactly as written, publicly under the brand.</p>
						</div>
					)}

					<div className='flex flex-col gap-2'>
						<Label className={FIELD_LABEL}>Select channels</Label>
						<MultiSelect
							className={cn('min-h-10', FIELD_TRIGGER)}
							options={channelOptions}
							defaultValue={form.channelIds}
							onValueChange={(value) => set('channelIds', value)}
							placeholder='All channels'
							maxCount={3}
						/>
						<p className={cn(FIELD_HINT, unscoped && isIrreversibleAction(effectiveAction) && FIELD_HINT_STRONG)}>
							{channelScopeHint(form.channelIds.length, effectiveAction)}
						</p>
					</div>

					{/*
					 * Tags and the action toggles are locked on a Kim-authored rule for the same
					 * reason its name and condition are: `save` sends ONLY `channelScope` for those
					 * rules, so anything else edited here was accepted by the form and then thrown
					 * away. Showing a live control that cannot persist is worse than showing a dead
					 * one — scoping the rule to channels is genuinely the only thing to change.
					 */}
					{config.tags && (
						<div className={cn('flex flex-col gap-2', !editable && 'opacity-50')}>
							<Label className={FIELD_LABEL}>{tagsRequired ? 'Add tags' : 'Add tags (optional)'}</Label>
							<TagCombobox
								value={form.tags}
								disabled={!editable}
								suggestions={tagSuggestions}
								onChange={(tags) => set('tags', tags)}
							/>
						</div>
					)}

					{config.like && (
						<ToggleRow
							label='Like comment'
							checked={form.like}
							disabled={!editable}
							onCheckedChange={(v) => set('like', v)}
						/>
					)}
					{config.closeAfterAction && (
						<ToggleRow
							label='Close ticket after action'
							checked={form.closeAfterAction}
							disabled={!editable}
							onCheckedChange={(v) => set('closeAfterAction', v)}
						/>
					)}
				</div>

				{/* No top border: the design divides the header from the content and nothing else, so a
				    second rule here made the footer read as a third section. p-3 and a 6px gap
				    between the two save actions, which belong together against Cancel. */}
				<div className='flex items-center justify-between p-3'>
					<Button variant='outline' className={PILL_BUTTON} onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<div className='flex items-center gap-1.5'>
						{/* Kim-authored rules have no draft state — the only thing to save is their scope. */}
						{editable && (
							<Button variant='outline' className={PILL_BUTTON} disabled={!canSave || saving !== null} onClick={() => save('draft')}>
								{saving === 'draft' && <Loader2 className='size-4 animate-spin' />} Save as Draft
							</Button>
						)}
						<Button className={PILL_BUTTON} disabled={!canSave || saving !== null} onClick={() => save('enable')}>
							{saving === 'enable' && <Loader2 className='size-4 animate-spin' />} Save &amp; Enable
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
